import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import T from '../styles/theme';
import Icon from '../components/Icon';
import { useStore } from '../data/store';
import { useAuth } from '../data/auth';
import supabase from '../data/supabase';
import {
  colLabel, colIndex, cellKey, parseCellRef, isErr,
  evaluateAllCells, formatCell, FUNCTION_LIST,
} from '../lib/sheetEngine';

/* ═══════════════════════════════════════════════════════════════════
   COREFLOW SHEETS — Notion-Inspired Spreadsheet UI
   Minimal chrome, contextual floating toolbar, progressive disclosure
   ═══════════════════════════════════════════════════════════════════ */

const DEFAULT_ROWS = 100;
const DEFAULT_COLS = 26;
const CELL_W = 120;
const CELL_H = 32;
const ROW_HEADER_W = 46;
const MAX_HISTORY = 50;

// ─── Undo/Redo Hook ─────────────────────────────────────────────
function useHistory(initial) {
  const [state, setState] = useState(initial);
  const past = useRef([]);
  const future = useRef([]);
  const set = useCallback((newState, skipHistory = false) => {
    setState(prev => {
      if (!skipHistory) { past.current = [...past.current.slice(-(MAX_HISTORY - 1)), prev]; future.current = []; }
      return typeof newState === "function" ? newState(prev) : newState;
    });
  }, []);
  const undo = useCallback(() => { setState(prev => { if (!past.current.length) return prev; const p = past.current.pop(); future.current.push(prev); return p; }); }, []);
  const redo = useCallback(() => { setState(prev => { if (!future.current.length) return prev; const f = future.current.pop(); past.current.push(prev); return f; }); }, []);
  return [state, set, undo, redo, () => past.current.length > 0, () => future.current.length > 0];
}

// ─── Formula Autocomplete Data ──────────────────────────────────
const FUNC_HINTS = FUNCTION_LIST.map(f => ({ name: f, prefix: f.split(".")[0] }));

// ═══════════════════════════════════════════════════════════════
//  SHEETS PAGE
// ═══════════════════════════════════════════════════════════════
export default function SheetsPage({ isMobile }) {
  const store = useStore();
  const auth = useAuth();
  const orgId = auth.currentUser?._orgId;
  const userId = auth.currentUser?._dbId;

  // ─── Sheet List ────────────────────────────────────────────
  const [sheets, setSheets] = useState([]);
  const [activeSheetId, setActiveSheetId] = useState(null);
  const [sheetName, setSheetName] = useState("Untitled Sheet");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ─── Grid State ────────────────────────────────────────────
  const [cellData, setCellData, undo, redo, canUndo, canRedo] = useHistory({});
  const [colWidths, setColWidths] = useState({});
  const [colFormats, setColFormats] = useState({});
  const [cellStyles, setCellStyles] = useState({});
  const [numRows, setNumRows] = useState(DEFAULT_ROWS);
  const [numCols, setNumCols] = useState(DEFAULT_COLS);
  const [freezeRow, setFreezeRow] = useState(0);

  // ─── Selection ─────────────────────────────────────────────
  const [selectedCell, setSelectedCell] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [formulaBarValue, setFormulaBarValue] = useState("");
  const [selStart, setSelStart] = useState(null);
  const [selEnd, setSelEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // ─── UI State ──────────────────────────────────────────────
  const [ctxMenu, setCtxMenu] = useState(null);
  const [showNewSheet, setShowNewSheet] = useState(false);
  const [newSheetName, setNewSheetName] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [autocomplete, setAutocomplete] = useState(null); // { matches: [], selectedIdx: 0 }
  const [resizingCol, setResizingCol] = useState(null);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const gridRef = useRef(null);
  const editRef = useRef(null);
  const renameRef = useRef(null);
  const saveTimer = useRef(null);

  // ─── Computed ──────────────────────────────────────────────
  const computed = useMemo(() => evaluateAllCells(cellData, { contacts: store.contacts, deals: store.deals }), [cellData, store.contacts, store.deals]);

  const selRange = useMemo(() => {
    if (!selStart) return null;
    const end = selEnd || selStart;
    return { r1: Math.min(selStart.row, end.row), r2: Math.max(selStart.row, end.row), c1: Math.min(selStart.col, end.col), c2: Math.max(selStart.col, end.col) };
  }, [selStart, selEnd]);

  const isCellInSel = (r, c) => selRange && r >= selRange.r1 && r <= selRange.r2 && c >= selRange.c1 && c <= selRange.c2;

  // ─── Load Sheets ───────────────────────────────────────────
  useEffect(() => {
    if (!orgId) return;
    (async () => {
      const { data } = await supabase.from("sheets").select("*").eq("org_id", orgId).order("updated_at", { ascending: false });
      setSheets(data || []);
      if (data?.length > 0) loadSheet(data[0]);
      setLoading(false);
    })();
  }, [orgId]); // eslint-disable-line

  const loadSheet = (sheet) => {
    setActiveSheetId(sheet.id); setSheetName(sheet.name || "Untitled Sheet");
    setCellData(sheet.cell_data || {}, true); setColWidths(sheet.col_widths || {});
    setColFormats(sheet.col_formats || {}); setCellStyles(sheet.cell_styles || {});
    setNumRows(sheet.num_rows || DEFAULT_ROWS); setNumCols(sheet.num_cols || DEFAULT_COLS);
    setFreezeRow(sheet.freeze_row || 0);
    setSelectedCell(null); setEditingCell(null); setSelStart(null); setSelEnd(null);
  };

  // ─── Save ──────────────────────────────────────────────────
  const saveSheet = useCallback(async (data) => {
    if (!activeSheetId || !orgId) return; setSaving(true);
    await supabase.from("sheets").update({
      cell_data: data || cellData, col_widths: colWidths, col_formats: colFormats,
      cell_styles: cellStyles, num_rows: numRows, num_cols: numCols, name: sheetName,
      freeze_row: freezeRow, updated_at: new Date().toISOString(),
    }).eq("id", activeSheetId);
    setSaving(false);
  }, [activeSheetId, orgId, cellData, colWidths, colFormats, cellStyles, numRows, numCols, sheetName, freezeRow]);

  const debouncedSave = useCallback((data) => { clearTimeout(saveTimer.current); saveTimer.current = setTimeout(() => saveSheet(data), 1200); }, [saveSheet]);

  // ─── Editing ───────────────────────────────────────────────
  const startEdit = useCallback((key) => {
    setEditingCell(key);
    const raw = cellData[key]?.raw ?? cellData[key] ?? "";
    setEditValue(String(raw)); setFormulaBarValue(String(raw));
    setTimeout(() => editRef.current?.focus(), 0);
  }, [cellData]);

  const commitEdit = useCallback(() => {
    if (!editingCell) return;
    const newData = { ...cellData };
    if (editValue === "" || editValue === undefined) delete newData[editingCell];
    else newData[editingCell] = { raw: editValue };
    setCellData(newData); setEditingCell(null); setAutocomplete(null); debouncedSave(newData);
  }, [editingCell, editValue, cellData, setCellData, debouncedSave]);

  const cancelEdit = () => { setEditingCell(null); setEditValue(""); setAutocomplete(null); };

  // ─── Formula Autocomplete ─────────────────────────────────
  const updateAutocomplete = useCallback((val) => {
    if (!val.startsWith("=")) { setAutocomplete(null); return; }
    // Find the last function-like token being typed
    const afterEq = val.slice(1);
    const match = afterEq.match(/([A-Z_.]+)$/i);
    if (!match || match[1].length < 2) { setAutocomplete(null); return; }
    const prefix = match[1].toUpperCase();
    const matches = FUNC_HINTS.filter(f => f.name.startsWith(prefix)).slice(0, 8);
    if (matches.length > 0) setAutocomplete({ matches, selectedIdx: 0, prefix });
    else setAutocomplete(null);
  }, []);

  const applyAutocomplete = useCallback((funcName) => {
    if (!autocomplete) return;
    const beforePrefix = editValue.slice(0, editValue.length - autocomplete.prefix.length);
    const newVal = beforePrefix + funcName + "(";
    setEditValue(newVal); setFormulaBarValue(newVal); setAutocomplete(null);
    setTimeout(() => editRef.current?.focus(), 0);
  }, [autocomplete, editValue]);

  // ─── Copy / Paste ──────────────────────────────────────────
  const handleCopy = useCallback((e) => {
    const range = selRange || (selectedCell ? (() => { const ref = parseCellRef(selectedCell); return ref ? { r1: ref.row, r2: ref.row, c1: ref.col, c2: ref.col } : null; })() : null);
    if (!range) return;
    const rows = [];
    for (let r = range.r1; r <= range.r2; r++) {
      const row = [];
      for (let c = range.c1; c <= range.c2; c++) row.push(String(computed[cellKey(r, c)]?.computed ?? ""));
      rows.push(row.join("\t"));
    }
    if (e?.clipboardData) { e.clipboardData.setData("text/plain", rows.join("\n")); e.preventDefault(); }
    else navigator.clipboard?.writeText(rows.join("\n"));
  }, [selRange, selectedCell, computed]);

  const handlePaste = useCallback((e) => {
    const text = e?.clipboardData?.getData("text/plain") || "";
    if (!text || !selectedCell) return; e.preventDefault();
    const ref = parseCellRef(selectedCell); if (!ref) return;
    const newData = { ...cellData };
    const rows = text.split("\n").map(r => r.split("\t"));
    rows.forEach((row, ri) => row.forEach((val, ci) => {
      const key = cellKey(ref.row + ri, ref.col + ci);
      if (val === "") delete newData[key]; else newData[key] = { raw: val };
    }));
    setCellData(newData); debouncedSave(newData);
    if (rows.length > 1 || (rows[0]?.length || 0) > 1) {
      setSelStart({ row: ref.row, col: ref.col });
      setSelEnd({ row: ref.row + rows.length - 1, col: ref.col + (rows[0]?.length || 1) - 1 });
    }
  }, [selectedCell, cellData, setCellData, debouncedSave]);

  // ─── Keyboard ──────────────────────────────────────────────
  const moveSelection = useCallback((dr, dc, extend = false) => {
    if (!selectedCell) return;
    const ref = parseCellRef(selectedCell); if (!ref) return;
    const base = extend && selEnd ? selEnd : ref;
    const nr = Math.max(0, Math.min(numRows - 1, (extend ? base.row : ref.row) + dr));
    const nc = Math.max(0, Math.min(numCols - 1, (extend ? base.col : ref.col) + dc));
    if (extend) { if (!selStart) setSelStart({ row: ref.row, col: ref.col }); setSelEnd({ row: nr, col: nc }); }
    else { const key = cellKey(nr, nc); setSelectedCell(key); setSelStart(null); setSelEnd(null); setFormulaBarValue(String(cellData[key]?.raw ?? cellData[key] ?? "")); }
  }, [selectedCell, selStart, selEnd, numRows, numCols, cellData]);

  const handleKeyDown = useCallback((e) => {
    // Autocomplete navigation
    if (autocomplete && editingCell) {
      if (e.key === "ArrowDown") { e.preventDefault(); setAutocomplete(prev => ({ ...prev, selectedIdx: Math.min(prev.selectedIdx + 1, prev.matches.length - 1) })); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setAutocomplete(prev => ({ ...prev, selectedIdx: Math.max(prev.selectedIdx - 1, 0) })); return; }
      if (e.key === "Tab" || e.key === "Enter") {
        if (autocomplete.matches.length > 0) { e.preventDefault(); applyAutocomplete(autocomplete.matches[autocomplete.selectedIdx].name); return; }
      }
      if (e.key === "Escape") { setAutocomplete(null); return; }
    }

    // Global shortcuts
    if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) { e.preventDefault(); undo(); return; }
    if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.key === "z" && e.shiftKey))) { e.preventDefault(); redo(); return; }
    if ((e.ctrlKey || e.metaKey) && e.key === "c") { handleCopy(e); return; }
    if ((e.ctrlKey || e.metaKey) && e.key === "b" && selectedCell) { e.preventDefault(); toggleStyle("bold"); return; }
    if ((e.ctrlKey || e.metaKey) && e.key === "i" && selectedCell) { e.preventDefault(); toggleStyle("italic"); return; }
    if ((e.ctrlKey || e.metaKey) && e.key === "u" && selectedCell) { e.preventDefault(); toggleStyle("underline"); return; }

    if (editingCell) {
      if (e.key === "Enter" && !autocomplete) { e.preventDefault(); commitEdit(); moveSelection(1, 0); }
      if (e.key === "Tab") { e.preventDefault(); commitEdit(); moveSelection(0, e.shiftKey ? -1 : 1); }
      if (e.key === "Escape") cancelEdit();
      return;
    }
    if (!selectedCell) return;
    if (e.key === "Enter") { e.preventDefault(); startEdit(selectedCell); return; }
    if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      const newData = { ...cellData };
      if (selRange) { for (let r = selRange.r1; r <= selRange.r2; r++) for (let c = selRange.c1; c <= selRange.c2; c++) delete newData[cellKey(r, c)]; }
      else delete newData[selectedCell];
      setCellData(newData); debouncedSave(newData); return;
    }
    if (e.key === "Tab") { e.preventDefault(); moveSelection(0, e.shiftKey ? -1 : 1); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); moveSelection(-1, 0, e.shiftKey); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); moveSelection(1, 0, e.shiftKey); return; }
    if (e.key === "ArrowLeft") { e.preventDefault(); moveSelection(0, -1, e.shiftKey); return; }
    if (e.key === "ArrowRight") { e.preventDefault(); moveSelection(0, 1, e.shiftKey); return; }
    if (e.key === "F2") { e.preventDefault(); startEdit(selectedCell); return; }
    // Type to start editing
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
      setEditValue(e.key); setEditingCell(selectedCell); setFormulaBarValue(e.key);
      if (e.key === "=") updateAutocomplete("=");
      setTimeout(() => editRef.current?.focus(), 0);
    }
  }, [editingCell, selectedCell, cellData, selRange, commitEdit, handleCopy, undo, redo, setCellData, debouncedSave, moveSelection, startEdit, autocomplete, applyAutocomplete, updateAutocomplete]); // eslint-disable-line

  // ─── Mouse ─────────────────────────────────────────────────
  const handleCellMouseDown = (r, c, e) => {
    if (e.button === 2) return;
    if (editingCell) commitEdit();
    const key = cellKey(r, c);
    setSelectedCell(key); setFormulaBarValue(String(cellData[key]?.raw ?? cellData[key] ?? ""));
    if (e.shiftKey && selStart) setSelEnd({ row: r, col: c });
    else { setSelStart({ row: r, col: c }); setSelEnd(null); setIsDragging(true); }
  };
  const handleCellMouseEnter = (r, c) => { if (isDragging) setSelEnd({ row: r, col: c }); };
  useEffect(() => { const up = () => setIsDragging(false); window.addEventListener("mouseup", up); return () => window.removeEventListener("mouseup", up); }, []);

  // ─── Column Resize ─────────────────────────────────────────
  const startResize = (ci, e) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX; const startW = colWidths[ci] || CELL_W;
    const onMove = (me) => setColWidths(prev => ({ ...prev, [ci]: Math.max(40, startW + me.clientX - startX) }));
    const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
    document.addEventListener("mousemove", onMove); document.addEventListener("mouseup", onUp);
  };

  // ─── Formatting (Notion-style: contextual) ─────────────────
  const getStyle = (key) => cellStyles[key] || {};
  const toggleStyle = useCallback((prop) => {
    const newStyles = { ...cellStyles };
    const apply = (key) => { newStyles[key] = { ...(newStyles[key] || {}), [prop]: !(newStyles[key]?.[prop]) }; };
    if (selRange) { for (let r = selRange.r1; r <= selRange.r2; r++) for (let c = selRange.c1; c <= selRange.c2; c++) apply(cellKey(r, c)); }
    else if (selectedCell) apply(selectedCell);
    setCellStyles(newStyles);
  }, [cellStyles, selRange, selectedCell]);

  const applyStyle = useCallback((prop, value) => {
    const newStyles = { ...cellStyles };
    const apply = (key) => { newStyles[key] = { ...(newStyles[key] || {}), [prop]: value }; };
    if (selRange) { for (let r = selRange.r1; r <= selRange.r2; r++) for (let c = selRange.c1; c <= selRange.c2; c++) apply(cellKey(r, c)); }
    else if (selectedCell) apply(selectedCell);
    setCellStyles(newStyles);
  }, [cellStyles, selRange, selectedCell]);

  const applyFormat = (fmt) => {
    const newFmt = { ...colFormats };
    if (selRange) { for (let c = selRange.c1; c <= selRange.c2; c++) newFmt[c] = fmt; }
    else if (selectedCell) { const ref = parseCellRef(selectedCell); if (ref) newFmt[ref.col] = fmt; }
    setColFormats(newFmt);
  };

  // ─── Context Menu ──────────────────────────────────────────
  const ctxAction = (fn) => () => { fn(); setCtxMenu(null); };
  const insertRow = (offset) => {
    const row = ctxMenu.row + offset;
    const newData = {}; const newStyles = {};
    Object.entries(cellData).forEach(([k, v]) => { const ref = parseCellRef(k); if (!ref) return; newData[ref.row >= row ? cellKey(ref.row + 1, ref.col) : k] = v; });
    Object.entries(cellStyles).forEach(([k, v]) => { const ref = parseCellRef(k); if (!ref) return; newStyles[ref.row >= row ? cellKey(ref.row + 1, ref.col) : k] = v; });
    setCellData(newData); setCellStyles(newStyles); setNumRows(numRows + 1); debouncedSave(newData);
  };
  const deleteRow = () => {
    const row = ctxMenu.row; const newData = {}; const newStyles = {};
    Object.entries(cellData).forEach(([k, v]) => { const ref = parseCellRef(k); if (!ref || ref.row === row) return; newData[ref.row > row ? cellKey(ref.row - 1, ref.col) : k] = v; });
    Object.entries(cellStyles).forEach(([k, v]) => { const ref = parseCellRef(k); if (!ref || ref.row === row) return; newStyles[ref.row > row ? cellKey(ref.row - 1, ref.col) : k] = v; });
    setCellData(newData); setCellStyles(newStyles); setNumRows(Math.max(1, numRows - 1)); debouncedSave(newData);
  };
  const insertCol = () => {
    const col = ctxMenu.col; const newData = {}; const newStyles = {};
    Object.entries(cellData).forEach(([k, v]) => { const ref = parseCellRef(k); if (!ref) return; newData[ref.col >= col ? cellKey(ref.row, ref.col + 1) : k] = v; });
    Object.entries(cellStyles).forEach(([k, v]) => { const ref = parseCellRef(k); if (!ref) return; newStyles[ref.col >= col ? cellKey(ref.row, ref.col + 1) : k] = v; });
    setCellData(newData); setCellStyles(newStyles); setNumCols(numCols + 1); debouncedSave(newData);
  };
  const deleteCol = () => {
    const col = ctxMenu.col; const newData = {}; const newStyles = {};
    Object.entries(cellData).forEach(([k, v]) => { const ref = parseCellRef(k); if (!ref || ref.col === col) return; newData[ref.col > col ? cellKey(ref.row, ref.col - 1) : k] = v; });
    Object.entries(cellStyles).forEach(([k, v]) => { const ref = parseCellRef(k); if (!ref || ref.col === col) return; newStyles[ref.col > col ? cellKey(ref.row, ref.col - 1) : k] = v; });
    setCellData(newData); setCellStyles(newStyles); setNumCols(Math.max(1, numCols - 1)); debouncedSave(newData);
  };
  const clearCells = () => {
    const newData = { ...cellData };
    if (selRange) { for (let r = selRange.r1; r <= selRange.r2; r++) for (let c = selRange.c1; c <= selRange.c2; c++) delete newData[cellKey(r, c)]; }
    else if (selectedCell) delete newData[selectedCell];
    setCellData(newData); debouncedSave(newData);
  };

  // ─── Sort ──────────────────────────────────────────────────
  const sortByCol = (ci) => {
    const dir = sortCol === ci && sortDir === "asc" ? "desc" : "asc";
    setSortCol(ci); setSortDir(dir);
    let maxRow = 0; Object.keys(cellData).forEach(k => { const ref = parseCellRef(k); if (ref) maxRow = Math.max(maxRow, ref.row); });
    const startRow = freezeRow > 0 ? freezeRow : 1;
    const rows = [];
    for (let r = startRow; r <= maxRow; r++) { const rowData = {}; for (let c = 0; c < numCols; c++) { const k = cellKey(r, c); if (cellData[k]) rowData[c] = cellData[k]; } rows.push({ data: rowData, sv: computed[cellKey(r, ci)]?.computed ?? "" }); }
    rows.sort((a, b) => { const na = Number(a.sv), nb = Number(b.sv); if (!isNaN(na) && !isNaN(nb)) return dir === "asc" ? na - nb : nb - na; return dir === "asc" ? String(a.sv).localeCompare(String(b.sv)) : String(b.sv).localeCompare(String(a.sv)); });
    const newData = {};
    Object.entries(cellData).forEach(([k, v]) => { const ref = parseCellRef(k); if (ref && ref.row < startRow) newData[k] = v; });
    rows.forEach((row, i) => { Object.entries(row.data).forEach(([c, v]) => { newData[cellKey(startRow + i, Number(c))] = v; }); });
    setCellData(newData); debouncedSave(newData);
  };

  // ─── Sheet CRUD ────────────────────────────────────────────
  const createSheet = async (name, importData = null) => {
    if (!orgId || !userId) return;
    const { data } = await supabase.from("sheets").insert({ org_id: orgId, created_by: userId, name: name || "Untitled Sheet", cell_data: importData || {}, col_widths: {}, col_formats: {}, cell_styles: {}, num_rows: DEFAULT_ROWS, num_cols: DEFAULT_COLS }).select().single();
    if (data) { setSheets(prev => [data, ...prev]); loadSheet(data); }
    setShowNewSheet(false); setNewSheetName("");
  };
  const deleteSheet = async (id) => {
    await supabase.from("sheets").delete().eq("id", id);
    setSheets(prev => prev.filter(s => s.id !== id));
    if (activeSheetId === id) { const rest = sheets.filter(s => s.id !== id); if (rest.length) loadSheet(rest[0]); else { setActiveSheetId(null); setCellData({}, true); } }
  };
  const handleImport = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const rows = ev.target.result.split("\n").map(r => r.split(",").map(c => c.replace(/^"|"$/g, "").trim()));
      const data = {}; rows.forEach((row, ri) => row.forEach((val, ci) => { if (val) data[cellKey(ri, ci)] = { raw: val }; }));
      createSheet(file.name.replace(/\.(csv|tsv)$/i, ""), data);
    };
    reader.readAsText(file); e.target.value = "";
  };
  const exportCSV = () => {
    let maxR = 0, maxC = 0;
    Object.keys(cellData).forEach(k => { const ref = parseCellRef(k); if (ref) { maxR = Math.max(maxR, ref.row); maxC = Math.max(maxC, ref.col); } });
    const rows = []; for (let r = 0; r <= maxR; r++) { const row = []; for (let c = 0; c <= maxC; c++) { const v = computed[cellKey(r, c)]?.computed ?? ""; row.push(String(v).includes(",") ? `"${v}"` : String(v)); } rows.push(row.join(",")); }
    const blob = new Blob([rows.join("\n")], { type: "text/csv" }); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = (sheetName || "sheet") + ".csv"; a.click(); URL.revokeObjectURL(url);
  };

  // ─── Sync formula bar ──────────────────────────────────────
  useEffect(() => { if (selectedCell && !editingCell) setFormulaBarValue(String(cellData[selectedCell]?.raw ?? cellData[selectedCell] ?? "")); }, [selectedCell, cellData, editingCell]);
  useEffect(() => { if (ctxMenu) { const close = () => setCtxMenu(null); document.addEventListener("click", close); return () => document.removeEventListener("click", close); } }, [ctxMenu]);

  // ─── Styles ────────────────────────────────────────────────
  const iS = { padding: "6px 10px", borderRadius: 6, border: "1px solid " + T.bdr, background: "transparent", color: T.txt, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" };
  const tBtn = (active = false) => ({
    padding: "3px 7px", borderRadius: 4, border: "none",
    background: active ? "rgba(64,89,173,0.20)" : "transparent",
    color: active ? "#FAFAFA" : "rgba(250,250,250,0.5)",
    fontSize: 12, cursor: "pointer", fontFamily: "inherit", minWidth: 26, textAlign: "center",
    transition: "all 0.1s",
  });

  // ─── Selection has content? Show floating toolbar ──────────
  const showFloatingToolbar = selectedCell && !editingCell;
  const selStyle = selectedCell ? getStyle(selectedCell) : {};

  // ═══ LOADING ═══
  if (loading) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "rgba(250,250,250,0.4)", fontSize: 13 }}>Loading sheets...</div>;

  // ═══ EMPTY STATE ═══
  if (sheets.length === 0 && !activeSheetId) return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", fontFamily: "inherit" }}>
      <div style={{ padding: 60, maxWidth: 480, margin: "0 auto", textAlign: "center" }}>
        <div style={{ width: 64, height: 64, borderRadius: 12, background: "rgba(64,89,173,0.08)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
          <Icon name="grid" size={28} color={T.blue} />
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 400, color: T.txt, margin: "0 0 8px" }}>CoreFlow Sheets</h2>
        <p style={{ fontSize: 13, color: "rgba(250,250,250,0.45)", lineHeight: 1.7, margin: "0 0 28px" }}>
          Spreadsheets powered by your CRM. Use formulas like =CF.CONTACT to pull live data.
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          <button onClick={() => createSheet("Untitled Sheet")} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: T.blue, color: "#FAFAFA", fontSize: 13, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>New Sheet</button>
          <label style={{ padding: "10px 24px", borderRadius: 8, border: "1px solid " + T.bdr, background: "transparent", color: "rgba(250,250,250,0.7)", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Import CSV<input type="file" accept=".csv,.tsv" onChange={handleImport} style={{ display: "none" }} /></label>
        </div>
      </div>
    </div>
  );

  // ═══ MAIN RENDER ═══
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", fontFamily: "inherit", outline: "none", background: "#2C2E33" }} tabIndex={0} onKeyDown={handleKeyDown} onPaste={handlePaste} onCopy={handleCopy}>

      {/* ═══ MENU BAR — sheet name + actions ═══ */}
      <div style={{ display: "flex", alignItems: "center", padding: "4px 12px", borderBottom: "1px solid rgba(217,219,221,0.08)", flexShrink: 0, gap: 6, background: "#2C2E33", minHeight: 34 }}>
        <Icon name="grid" size={16} color={T.blue} />
        {renaming ? (
          <input ref={renameRef} value={sheetName} onChange={e => setSheetName(e.target.value)}
            onBlur={() => { setRenaming(false); saveSheet(); }}
            onKeyDown={e => { if (e.key === "Enter") { setRenaming(false); saveSheet(); } e.stopPropagation(); }}
            autoFocus style={{ ...iS, fontSize: 13, width: 200, padding: "2px 8px" }} />
        ) : (
          <div onDoubleClick={() => setRenaming(true)} style={{ fontSize: 13, fontWeight: 400, color: T.txt, cursor: "text", padding: "2px 8px", borderRadius: 4 }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            {sheetName}
          </div>
        )}
        <div style={{ flex: 1 }} />
        {saving && <span style={{ fontSize: 10, color: "rgba(250,250,250,0.3)" }}>Saving...</span>}
        <button onClick={undo} disabled={!canUndo()} style={{ ...tBtn(), opacity: canUndo() ? 1 : 0.3 }} title="Undo (⌘Z)">↩</button>
        <button onClick={redo} disabled={!canRedo()} style={{ ...tBtn(), opacity: canRedo() ? 1 : 0.3 }} title="Redo (⌘Y)">↪</button>
        <div style={{ width: 1, height: 16, background: "rgba(217,219,221,0.12)" }} />
        <label style={{ ...tBtn(), cursor: "pointer" }} title="Import CSV">
          <Icon name="upload" size={12} color="rgba(250,250,250,0.5)" />
          <input type="file" accept=".csv,.tsv" onChange={handleImport} style={{ display: "none" }} />
        </label>
        <button onClick={exportCSV} style={tBtn()} title="Export CSV"><Icon name="download" size={12} color="rgba(250,250,250,0.5)" /></button>
      </div>

      {/* ═══ TOOLBAR — Google Sheets-style, always visible ═══ */}
      <div style={{ display: "flex", alignItems: "center", gap: 2, padding: "3px 12px", borderBottom: "1px solid rgba(217,219,221,0.08)", flexShrink: 0, background: "#2C2E33", flexWrap: "wrap", minHeight: 32 }}>
        {/* Text formatting */}
        <button onClick={() => toggleStyle("bold")} style={tBtn(selStyle.bold)} title="Bold (⌘B)"><b>B</b></button>
        <button onClick={() => toggleStyle("italic")} style={tBtn(selStyle.italic)} title="Italic (⌘I)"><i>I</i></button>
        <button onClick={() => toggleStyle("underline")} style={tBtn(selStyle.underline)} title="Underline (⌘U)"><u>U</u></button>
        <button onClick={() => toggleStyle("strikethrough")} style={tBtn(selStyle.strikethrough)} title="Strikethrough"><s>S</s></button>
        <div style={{ width: 1, height: 18, background: "rgba(217,219,221,0.12)", margin: "0 3px" }} />
        {/* Alignment */}
        <button onClick={() => applyStyle("align", "left")} style={tBtn(selStyle.align === "left" || !selStyle.align)} title="Align left">
          <svg width="12" height="12" viewBox="0 0 12 12"><path d="M1 2h10M1 5h6M1 8h8M1 11h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
        </button>
        <button onClick={() => applyStyle("align", "center")} style={tBtn(selStyle.align === "center")} title="Align center">
          <svg width="12" height="12" viewBox="0 0 12 12"><path d="M1 2h10M3 5h6M2 8h8M4 11h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
        </button>
        <button onClick={() => applyStyle("align", "right")} style={tBtn(selStyle.align === "right")} title="Align right">
          <svg width="12" height="12" viewBox="0 0 12 12"><path d="M1 2h10M5 5h6M3 8h8M7 11h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
        </button>
        <div style={{ width: 1, height: 18, background: "rgba(217,219,221,0.12)", margin: "0 3px" }} />
        {/* Colors */}
        <div style={{ position: "relative", display: "inline-flex" }}>
          <button style={tBtn()} title="Text color">
            <span style={{ borderBottom: `2px solid ${selStyle.color || "#FAFAFA"}`, paddingBottom: 1 }}>A</span>
            <input type="color" value={selStyle.color || "#FAFAFA"} onChange={e => applyStyle("color", e.target.value)}
              style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }} />
          </button>
        </div>
        <div style={{ position: "relative", display: "inline-flex" }}>
          <button style={tBtn(!!selStyle.bg)} title="Fill color">
            <span style={{ background: selStyle.bg || "transparent", padding: "1px 4px", borderRadius: 2, lineHeight: 1 }}>⬛</span>
            <input type="color" value={selStyle.bg || "#2C2E33"} onChange={e => applyStyle("bg", e.target.value)}
              style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }} />
          </button>
        </div>
        <div style={{ width: 1, height: 18, background: "rgba(217,219,221,0.12)", margin: "0 3px" }} />
        {/* Number format */}
        <select onChange={e => applyFormat(e.target.value)} value={(selectedCell && colFormats[parseCellRef(selectedCell)?.col]) || "auto"}
          style={{ background: "#33363D", border: "1px solid rgba(217,219,221,0.08)", borderRadius: 4, color: "rgba(250,250,250,0.6)", fontSize: 11, cursor: "pointer", fontFamily: "inherit", outline: "none", padding: "2px 6px" }}>
          <option value="auto" style={{ background: "#33363D" }}>Auto</option>
          <option value="currency" style={{ background: "#33363D" }}>$ Currency</option>
          <option value="percent" style={{ background: "#33363D" }}>% Percent</option>
          <option value="date" style={{ background: "#33363D" }}>Date</option>
        </select>
        <div style={{ width: 1, height: 18, background: "rgba(217,219,221,0.12)", margin: "0 3px" }} />
        <button onClick={() => setFreezeRow(freezeRow > 0 ? 0 : 1)} style={tBtn(freezeRow > 0)} title={freezeRow > 0 ? "Unfreeze rows" : "Freeze first row"}>❄</button>
      </div>

      {/* ═══ FORMULA BAR ═══ */}
      <div style={{ display: "flex", alignItems: "center", gap: 0, borderBottom: "1px solid rgba(217,219,221,0.10)", flexShrink: 0, background: "#25272B" }}>
        <div style={{ fontSize: 11, color: T.blue, minWidth: 56, textAlign: "center", padding: "6px 10px", borderRight: "1px solid rgba(217,219,221,0.10)", fontFamily: "monospace", background: "#25272B", fontWeight: 400 }}>{selectedCell || "—"}</div>
        <div style={{ fontSize: 12, color: "rgba(250,250,250,0.30)", padding: "0 8px", borderRight: "1px solid rgba(217,219,221,0.10)" }}>fx</div>
        <input value={editingCell ? editValue : formulaBarValue}
          onChange={e => {
            const val = e.target.value;
            if (editingCell) { setEditValue(val); setFormulaBarValue(val); updateAutocomplete(val); }
            else if (selectedCell) { setEditValue(val); setFormulaBarValue(val); setEditingCell(selectedCell); updateAutocomplete(val); }
          }}
          onKeyDown={e => { if (e.key === "Enter") { commitEdit(); e.target.blur(); } if (e.key === "Escape") { cancelEdit(); e.target.blur(); } e.stopPropagation(); }}
          style={{ flex: 1, padding: "6px 10px", border: "none", background: "#25272B", color: T.txt, fontSize: 12, outline: "none", fontFamily: "monospace" }}
          placeholder="Enter value or formula..." />
      </div>

      {/* ═══ GRID ═══ */}
      <div ref={gridRef} style={{ flex: 1, overflow: "auto", position: "relative", background: "#1E2025" }} onContextMenu={e => e.preventDefault()}>
        <table style={{ borderCollapse: "collapse", tableLayout: "fixed", width: ROW_HEADER_W + numCols * CELL_W }}>
          <thead>
            <tr style={{ position: "sticky", top: 0, zIndex: 3 }}>
              <th style={{ width: ROW_HEADER_W, minWidth: ROW_HEADER_W, height: CELL_H, background: "#25272B", borderBottom: "2px solid rgba(217,219,221,0.12)", borderRight: "1px solid rgba(217,219,221,0.10)", position: "sticky", left: 0, zIndex: 4 }} />
              {Array.from({ length: numCols }, (_, i) => (
                <th key={i} onClick={() => sortByCol(i)} style={{
                  width: colWidths[i] || CELL_W, minWidth: colWidths[i] || CELL_W, height: CELL_H, position: "relative",
                  background: "#25272B", borderBottom: "2px solid rgba(217,219,221,0.12)", borderRight: "1px solid rgba(217,219,221,0.08)",
                  fontSize: 11, fontWeight: 500, color: sortCol === i ? T.blue : "rgba(250,250,250,0.45)", textAlign: "center", cursor: "pointer", userSelect: "none",
                  letterSpacing: "0.02em",
                }}>
                  {colLabel(i)}{sortCol === i && <span style={{ marginLeft: 2, fontSize: 9 }}>{sortDir === "asc" ? "▲" : "▼"}</span>}
                  <div onMouseDown={e => startResize(i, e)} style={{ position: "absolute", top: 0, right: -2, width: 5, height: "100%", cursor: "col-resize", zIndex: 5 }} />
                </th>
              ))}
            </tr>
            {freezeRow > 0 && Array.from({ length: freezeRow }, (_, ri) => (
              <tr key={"fr-" + ri} style={{ position: "sticky", top: CELL_H, zIndex: 3 }}>
                <td style={{ width: ROW_HEADER_W, height: CELL_H, background: "#25272B", borderRight: "1px solid rgba(217,219,221,0.10)", borderBottom: "2px solid rgba(64,89,173,0.30)", fontSize: 10, color: T.blue, textAlign: "center", position: "sticky", left: 0, zIndex: 4, fontWeight: 500 }}>{ri + 1}</td>
                {Array.from({ length: numCols }, (_, ci) => {
                  const key = cellKey(ri, ci); const cell = computed[key]; const st = getStyle(key);
                  return <td key={ci} style={{ width: colWidths[ci] || CELL_W, height: CELL_H, borderRight: "1px solid rgba(217,219,221,0.08)", borderBottom: "2px solid rgba(64,89,173,0.30)", padding: "0 8px", fontSize: 12, fontWeight: st.bold ? 600 : 400, fontStyle: st.italic ? "italic" : "normal", textDecoration: st.underline ? "underline" : "none", color: st.color || T.txt, background: st.bg || "#25272B", textAlign: st.align || (typeof cell?.computed === "number" ? "right" : "left"), overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{cell ? formatCell(cell.computed, colFormats[ci]) : ""}</td>;
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {Array.from({ length: numRows }, (_, ri) => {
              if (ri < freezeRow) return null;
              return (
                <tr key={ri}>
                  <td style={{ width: ROW_HEADER_W, height: CELL_H, background: "#25272B", borderRight: "1px solid rgba(217,219,221,0.10)", borderBottom: "1px solid rgba(217,219,221,0.05)", fontSize: 10, color: "rgba(250,250,250,0.35)", textAlign: "center", position: "sticky", left: 0, zIndex: 1, userSelect: "none", fontWeight: 400 }}>{ri + 1}</td>
                  {Array.from({ length: numCols }, (_, ci) => {
                    const key = cellKey(ri, ci); const cell = computed[key]; const st = getStyle(key);
                    const isSel = selectedCell === key; const isEdit = editingCell === key;
                    const inSel = isCellInSel(ri, ci);
                    const display = cell ? formatCell(cell.computed, colFormats[ci]) : "";
                    const align = st.align || (typeof cell?.computed === "number" ? "right" : "left");

                    return (
                      <td key={ci}
                        onMouseDown={e => handleCellMouseDown(ri, ci, e)}
                        onMouseEnter={() => handleCellMouseEnter(ri, ci)}
                        onDoubleClick={() => startEdit(key)}
                        onContextMenu={e => { e.preventDefault(); setSelectedCell(key); setCtxMenu({ x: e.clientX, y: e.clientY, row: ri, col: ci }); }}
                        style={{
                          width: colWidths[ci] || CELL_W, minWidth: colWidths[ci] || CELL_W, height: CELL_H,
                          borderRight: "1px solid rgba(217,219,221,0.06)", borderBottom: "1px solid rgba(217,219,221,0.05)",
                          padding: 0, position: "relative", cursor: "cell",
                          background: isSel ? "rgba(64,89,173,0.12)" : inSel ? "rgba(64,89,173,0.06)" : (st.bg || "transparent"),
                          outline: isSel ? "2px solid #4059AD" : "none", outlineOffset: -1,
                        }}>
                        {isEdit ? (
                          <input ref={editRef} value={editValue}
                            onChange={e => { setEditValue(e.target.value); setFormulaBarValue(e.target.value); updateAutocomplete(e.target.value); }}
                            onBlur={() => { setTimeout(commitEdit, 100); }}
                            onKeyDown={e => {
                              if (autocomplete) {
                                if (e.key === "ArrowDown") { e.preventDefault(); e.stopPropagation(); setAutocomplete(p => ({ ...p, selectedIdx: Math.min(p.selectedIdx + 1, p.matches.length - 1) })); return; }
                                if (e.key === "ArrowUp") { e.preventDefault(); e.stopPropagation(); setAutocomplete(p => ({ ...p, selectedIdx: Math.max(p.selectedIdx - 1, 0) })); return; }
                                if (e.key === "Tab") { e.preventDefault(); e.stopPropagation(); applyAutocomplete(autocomplete.matches[autocomplete.selectedIdx]?.name); return; }
                              }
                              if (e.key === "Enter") { e.preventDefault(); commitEdit(); moveSelection(1, 0); }
                              if (e.key === "Tab") { e.preventDefault(); commitEdit(); moveSelection(0, e.shiftKey ? -1 : 1); }
                              if (e.key === "Escape") cancelEdit();
                              e.stopPropagation();
                            }}
                            style={{ width: "100%", height: "100%", border: "none", outline: "none", background: "rgba(64,89,173,0.08)", color: T.txt, fontSize: 12, padding: "0 8px", fontFamily: "monospace", boxSizing: "border-box" }} />
                        ) : (
                          <div style={{
                            width: "100%", height: "100%", padding: "0 8px", display: "flex", alignItems: "center",
                            fontSize: 12, fontWeight: st.bold ? 600 : 400, fontStyle: st.italic ? "italic" : "normal",
                            textDecoration: st.underline ? "underline" : (st.strikethrough ? "line-through" : "none"),
                            color: cell?.error ? "#E53935" : (st.color || T.txt),
                            justifyContent: align === "right" ? "flex-end" : align === "center" ? "center" : "flex-start",
                            overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
                          }}>{display}</div>
                        )}

                        {/* Autocomplete dropdown */}
                        {isEdit && autocomplete && autocomplete.matches.length > 0 && (
                          <div style={{ position: "absolute", top: CELL_H, left: 0, zIndex: 100, background: "#33363D", border: "1px solid " + T.bdr, borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.5)", overflow: "hidden", minWidth: 200 }}>
                            {autocomplete.matches.map((m, i) => (
                              <div key={m.name} onMouseDown={e => { e.preventDefault(); applyAutocomplete(m.name); }}
                                style={{
                                  padding: "7px 12px", fontSize: 12, fontFamily: "monospace", cursor: "pointer",
                                  background: i === autocomplete.selectedIdx ? "rgba(64,89,173,0.15)" : "transparent",
                                  color: i === autocomplete.selectedIdx ? T.txt : "rgba(250,250,250,0.6)",
                                }}>{m.name}</div>
                            ))}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ═══ BOTTOM SHEET TABS — Google Sheets style ═══ */}
      <div style={{ display: "flex", alignItems: "center", gap: 0, borderTop: "1px solid rgba(217,219,221,0.10)", flexShrink: 0, background: "#25272B", height: 32, overflowX: "auto" }}>
        <button onClick={() => setShowNewSheet(true)} style={{ width: 32, height: 32, border: "none", borderRight: "1px solid rgba(217,219,221,0.10)", background: "transparent", color: "rgba(250,250,250,0.5)", fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }} title="New sheet">+</button>
        {sheets.map(s => (
          <div key={s.id} onClick={() => loadSheet(s)} style={{
            padding: "0 16px", height: 32, display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 11, fontFamily: "inherit",
            color: s.id === activeSheetId ? T.txt : "rgba(250,250,250,0.45)",
            background: s.id === activeSheetId ? "#2C2E33" : "transparent",
            borderRight: "1px solid rgba(217,219,221,0.08)",
            borderTop: s.id === activeSheetId ? "2px solid " + T.blue : "2px solid transparent",
            fontWeight: s.id === activeSheetId ? 500 : 400,
            flexShrink: 0, whiteSpace: "nowrap",
          }}>
            <span>{s.name || "Untitled"}</span>
            {sheets.length > 1 && <span onClick={e => { e.stopPropagation(); if (confirm("Delete \"" + s.name + "\"?")) deleteSheet(s.id); }} style={{ fontSize: 10, color: "rgba(250,250,250,0.2)", cursor: "pointer", padding: "0 2px" }} onMouseEnter={e => e.currentTarget.style.color = "#E53935"} onMouseLeave={e => e.currentTarget.style.color = "rgba(250,250,250,0.2)"}>×</span>}
          </div>
        ))}
      </div>

      {/* ═══ CONTEXT MENU ═══ */}
      {ctxMenu && ReactDOM.createPortal(
        <div style={{ position: "fixed", top: ctxMenu.y, left: ctxMenu.x, zIndex: 9999, background: "#33363D", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 10, boxShadow: "0 12px 40px rgba(0,0,0,0.60)", overflow: "hidden", minWidth: 180, padding: "4px 0" }}>
          {[
            { label: "Copy", fn: ctxAction(() => handleCopy()), key: "⌘C" },
            { label: "Clear", fn: ctxAction(clearCells), key: "Del" },
            null,
            { label: "Insert row above", fn: ctxAction(() => insertRow(0)) },
            { label: "Insert row below", fn: ctxAction(() => insertRow(1)) },
            { label: "Delete row", fn: ctxAction(deleteRow) },
            null,
            { label: "Insert column", fn: ctxAction(insertCol) },
            { label: "Delete column", fn: ctxAction(deleteCol) },
            null,
            { label: "Sort ascending", fn: ctxAction(() => sortByCol(ctxMenu.col)) },
          ].map((item, i) => item === null ? (
            <div key={i} style={{ height: 1, background: "rgba(217,219,221,0.06)", margin: "2px 0" }} />
          ) : (
            <div key={i} onClick={item.fn} style={{ padding: "7px 14px", fontSize: 12, color: "rgba(250,250,250,0.8)", cursor: "pointer", display: "flex", justifyContent: "space-between" }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.05)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <span>{item.label}</span>
              {item.key && <span style={{ fontSize: 10, color: "rgba(250,250,250,0.25)" }}>{item.key}</span>}
            </div>
          ))}
        </div>, document.body
      )}

      {/* ═══ NEW SHEET MODAL ═══ */}
      {showNewSheet && ReactDOM.createPortal(
        <div style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={() => setShowNewSheet(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.50)", backdropFilter: "blur(4px)" }} />
          <div style={{ position: "relative", width: 360, background: "#2C2E33", border: "1px solid " + T.bdr, borderRadius: 12, padding: "24px 28px", boxShadow: "0 40px 100px rgba(0,0,0,0.40)" }}>
            <div style={{ fontSize: 15, fontWeight: 400, color: T.txt, marginBottom: 16 }}>New Sheet</div>
            <input value={newSheetName} onChange={e => setNewSheetName(e.target.value)} placeholder="Sheet name..." autoFocus
              onKeyDown={e => { if (e.key === "Enter") createSheet(newSheetName || "Untitled Sheet"); e.stopPropagation(); }}
              style={{ ...iS, width: "100%", marginBottom: 16 }} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setShowNewSheet(false)} style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid " + T.bdr, background: "transparent", color: "rgba(250,250,250,0.6)", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={() => createSheet(newSheetName || "Untitled Sheet")} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: T.blue, color: "#fff", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Create</button>
            </div>
          </div>
        </div>, document.body
      )}
    </div>
  );
}
